import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Switch, Typography } from "@mui/material";
import { Select, toast } from "@outpave/ds";
import {
  GET_COMPANY_CARD_PROGRAM_SETTINGS,
  SET_CARD_PROGRAM_CARD_HOLDER_TYPE,
  SET_CARD_PROGRAM_DESIGN,
  SET_COMPANY_CARD_PROGRAM_ACCESS,
} from "graphql/platformIntegration";
import {
  GET_COMPANY_CHARGE_REPAYMENT_PROVIDER,
  SET_COMPANY_CHARGE_REPAYMENT_PROVIDER,
} from "graphql/company";
import CardHolderType from "enums/CardHolderType";
import CardProgramAccess from "enums/CardProgramAccess";
import ApplicationType from "enums/ApplicationType";
import ChargeRepaymentProvider from "enums/ChargeRepaymentProvider";
import { chargeRepaymentProviderLabel } from "utils/chargeRepayment/chargeRepaymentProviderLabel";
import { CARD_HOLDER_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";
import {
  SettingRow,
  SettingRows,
  SettingsCard,
} from "components/platform/accounts/SettingsCard";

// A row's picker keeps one width, so the controls line up down the card.
const SETTING_SELECT_WIDTH = 240;

// Stands for "no override" in the picker; it is never sent as a provider.
const USE_CARD_ISSUER_SETTING = "USE_CARD_ISSUER_SETTING";

// Stands for "no design of our own" in the picker; choosing it clears the program's design.
const ISSUER_DEFAULT_DESIGN = "ISSUER_DEFAULT_DESIGN";

interface CardDesignOption {
  id: string;
  name: string;
}

/** A program the company holds: who its new cards go to and the design they get, each with what its issuer offers. */
interface CompanyCardProgram {
  id: string;
  nickname?: string | null;
  cardHolderType: CardHolderType;
  cardHolderTypeOptions: CardHolderType[];
  cardDesign?: CardDesignOption | null;
  cardDesignOptions: CardDesignOption[];
}

interface CompanyCardProgramSetting {
  platformIntegrationId: string;
  integrationName: string;
  productKey: string;
  displayName: string;
  subAccountType?: string | null;
  cardHolderTypes: CardHolderType[];
  platformEnabled: boolean;
  access: CardProgramAccess;
  program?: CompanyCardProgram | null;
}

/**
 * The card programs one company may open, and how each one it holds issues, on
 * its account's Card programs tab. The platform's own switch is the master, so
 * this can only take a program away from the company.
 */
const CardProgramsSettings: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { t } = useTranslation();
  const {
    data,
    loading,
    error: loadError,
  } = useQuery(GET_COMPANY_CARD_PROGRAM_SETTINGS, {
    variables: { companyId },
    fetchPolicy: "network-only",
  });
  const [setAccess, { loading: saving }] = useMutation(
    SET_COMPANY_CARD_PROGRAM_ACCESS,
    {
      update: (cache, { data: result }) => {
        cache.writeQuery({
          query: GET_COMPANY_CARD_PROGRAM_SETTINGS,
          variables: { companyId },
          data: {
            companyCardProgramSettings: result?.setCompanyCardProgramAccess,
          },
        });
      },
    },
  );

  const { data: repaymentProviderData } = useQuery(
    GET_COMPANY_CHARGE_REPAYMENT_PROVIDER,
    {
      variables: { id: companyId },
      fetchPolicy: "network-only",
    },
  );
  const [setRepaymentProvider, { loading: savingRepaymentProvider }] =
    useMutation(SET_COMPANY_CHARGE_REPAYMENT_PROVIDER, {
      onCompleted: () => toast.success(t("ChargeRepaymentProviderSavedToast")),
      onError: (error) =>
        toast.error(error?.message || t("ChargeRepaymentProviderSaveError")),
    });

  const [setProgramDesign, { loading: savingDesign }] = useMutation(
    SET_CARD_PROGRAM_DESIGN,
    {
      onError: (error) => toast.error(error?.message || t("Please try again")),
    },
  );
  const [setProgramCardHolderType, { loading: savingCardHolderType }] =
    useMutation(SET_CARD_PROGRAM_CARD_HOLDER_TYPE, {
      onCompleted: () => toast.success(t("CompanyCardHoldersSavedToast")),
      onError: (error) => toast.error(error?.message || t("Please try again")),
    });

  const settings: CompanyCardProgramSetting[] =
    data?.companyCardProgramSettings ?? [];

  // Only a charge program has a balance a company pays down.
  const offersCharge = settings.some(
    (setting) => setting.subAccountType === ApplicationType.CHARGE,
  );
  const repaymentProviderOptions = [
    { id: USE_CARD_ISSUER_SETTING, label: t("UseCardIssuerSetting") },
    ...Object.values(ChargeRepaymentProvider).map((provider) => ({
      id: provider,
      label: chargeRepaymentProviderLabel(provider),
    })),
  ];

  const toggle = async (setting: CompanyCardProgramSetting) => {
    try {
      await setAccess({
        variables: {
          companyId,
          platformIntegrationId: setting.platformIntegrationId,
          productKey: setting.productKey,
          access:
            setting.access === CardProgramAccess.DISABLED
              ? CardProgramAccess.FOLLOW_PLATFORM
              : CardProgramAccess.DISABLED,
        },
      });
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("Please try again"),
      );
    }
  };

  return (
    <Stack gap="var(--space-5)">
      {!data && loading && <CustomSkeleton />}
      {!data && loadError && (
        <Alert severity="error" role="status">
          {t("AccountCardProgramsLoadError")}
        </Alert>
      )}
      {data && !settings.length && (
        <Typography variant="body2">{t("NoCardProgramsOnPlatform")}</Typography>
      )}

      {settings.map((setting) => {
        const program = setting.program;
        const name = program?.nickname || setting.displayName;
        return (
          <SettingsCard
            key={`${setting.platformIntegrationId}-${setting.productKey}`}
            title={name}
            subtitle={
              setting.platformEnabled
                ? [
                    setting.subAccountType && t(setting.subAccountType),
                    setting.integrationName !== setting.displayName &&
                      setting.integrationName,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : t("Off for the platform")
            }
            action={
              <Switch
                checked={
                  setting.platformEnabled &&
                  setting.access !== CardProgramAccess.DISABLED
                }
                disabled={saving || !setting.platformEnabled}
                onChange={() => void toggle(setting)}
                slotProps={{
                  input: {
                    "aria-label": t("AvailableToAccountFor", {
                      program: name,
                      interpolation: { escapeValue: false },
                    }),
                  },
                }}
              />
            }
          >
            {program && (
              <SettingRows>
                <SettingRow
                  title={t("Cardholders")}
                  control={() => (
                    <Select
                      aria-label={t("CardHoldersFor", {
                        program: name,
                        interpolation: { escapeValue: false },
                      })}
                      size="md"
                      width={SETTING_SELECT_WIDTH}
                      clearable={false}
                      disabled={savingCardHolderType}
                      value={program.cardHolderType}
                      options={program.cardHolderTypeOptions.map((type) => ({
                        id: type,
                        label: t(CARD_HOLDER_LABEL_KEY[type]),
                      }))}
                      onChange={(id) => {
                        if (!id || id === program.cardHolderType) return;
                        setProgramCardHolderType({
                          variables: {
                            companyId,
                            programId: program.id,
                            cardHolderType: id,
                          },
                        });
                      }}
                    />
                  )}
                />
                <SettingRow
                  title={t("Card design")}
                  divider={false}
                  control={() => (
                    <CardDesignSelect
                      program={program}
                      name={name}
                      disabled={savingDesign}
                      onChange={(designId) =>
                        setProgramDesign({
                          variables: {
                            companyId,
                            programId: program.id,
                            designId,
                          },
                        })
                      }
                    />
                  )}
                />
              </SettingRows>
            )}
          </SettingsCard>
        );
      })}

      {offersCharge && (
        <SettingsCard title={t("Credit repayments")}>
          <SettingRows>
            <SettingRow
              title={t("Repayment provider")}
              description={t("ChargeRepaymentAccountHelp")}
              divider={false}
              control={() => (
                <Select
                  aria-label={t("Repayment provider")}
                  size="md"
                  width={SETTING_SELECT_WIDTH}
                  clearable={false}
                  disabled={savingRepaymentProvider || !repaymentProviderData}
                  options={repaymentProviderOptions}
                  value={
                    repaymentProviderData
                      ? (repaymentProviderData.company
                          ?.chargeRepaymentProviderOverride ??
                        USE_CARD_ISSUER_SETTING)
                      : null
                  }
                  onChange={(id) => {
                    if (!id) return;
                    setRepaymentProvider({
                      variables: {
                        companyId,
                        provider: id === USE_CARD_ISSUER_SETTING ? null : id,
                      },
                      refetchQueries: [GET_COMPANY_CHARGE_REPAYMENT_PROVIDER],
                    });
                  }}
                />
              )}
            />
          </SettingRows>
        </SettingsCard>
      )}
    </Stack>
  );
};

const CardDesignSelect: React.FC<{
  program: CompanyCardProgram;
  name: string;
  disabled: boolean;
  onChange: (designId: string | null) => void;
}> = ({ program, name, disabled, onChange }) => {
  const { t } = useTranslation();
  // A saved design the issuer no longer lists stays visible, and so can still be cleared.
  const saved = program.cardDesign;
  const designs =
    saved && !program.cardDesignOptions.some(({ id }) => id === saved.id)
      ? [...program.cardDesignOptions, saved]
      : program.cardDesignOptions;

  return (
    <Select
      aria-label={t("CardDesignFor", {
        program: name,
        interpolation: { escapeValue: false },
      })}
      size="md"
      width={SETTING_SELECT_WIDTH}
      clearable={false}
      disabled={disabled || !designs.length}
      options={[
        { id: ISSUER_DEFAULT_DESIGN, label: t("IssuerDefaultCardDesign") },
        ...designs.map((design) => ({ id: design.id, label: design.name })),
      ]}
      value={saved?.id ?? ISSUER_DEFAULT_DESIGN}
      onChange={(id) => {
        if (!id || id === (saved?.id ?? ISSUER_DEFAULT_DESIGN)) return;
        onChange(id === ISSUER_DEFAULT_DESIGN ? null : id);
      }}
    />
  );
};

export default CardProgramsSettings;
